/**
 * Canonical JSON and SHA-256 for observations, rule content and parameter bindings — ADR-0088 §2–§3.
 *
 * Synchronous (plain-TypeScript SHA-256), so evaluation stays one pure, deterministic function.
 */
import { canonicalJson } from '../utils/audit-crypto.js';
import { sha256HexOfChunks } from '../utils/sha256-stream.js';
import { scrubSecrets } from '../utils/secret-filter.js';
const enc = new TextEncoder();
export function sha256Text(text) {
    return sha256HexOfChunks([enc.encode(text)]);
}
export function canonical(value) {
    return canonicalJson(value);
}
/** Values larger than this go to object storage (fast follow); the slice keeps values small and refuses larger ones. */
export const OBSERVATION_VALUE_MAX_BYTES = 8192;
export class ObservationValueError extends Error {
    /**
     * Where the refused reading is (V109-3 security L-5): the observation path — or null when the path
     * itself looks like a secret — and which part of it was refused. Never the value.
     */
    path = null;
    field = null;
    /** True when the secret / e-mail guard refused it (vs a type or size problem). */
    secretShaped = false;
    constructor(message, where) {
        super(message);
        this.name = 'ObservationValueError';
        if (where) {
            this.path = where.path;
            this.field = where.field;
            this.secretShaped = where.secretShaped ?? false;
        }
    }
}
/** The observation path, safe to show: null when the path itself would be scrubbed. */
function safePath(path) {
    return scrubSecrets(path) === path ? path.slice(0, 200) : null;
}
/** The value in its canonical form for its type (string sets sorted and de-duplicated). */
export function normalizeValue(value, type) {
    if (value === null)
        return null;
    switch (type) {
        case 'null': return null;
        case 'bool':
            if (typeof value !== 'boolean')
                throw new ObservationValueError(`bool expected, got ${typeof value}`);
            return value;
        case 'int':
        case 'duration_s':
            if (typeof value !== 'number' || !Number.isInteger(value))
                throw new ObservationValueError(`${type} expected an integer`);
            return value;
        case 'number':
            if (typeof value !== 'number' || !Number.isFinite(value))
                throw new ObservationValueError('number expected');
            return value;
        case 'string':
            if (typeof value !== 'string')
                throw new ObservationValueError('string expected');
            return value;
        case 'timestamp': {
            if (typeof value !== 'string' || Number.isNaN(Date.parse(value)))
                throw new ObservationValueError('timestamp expected (ISO 8601)');
            return new Date(value).toISOString();
        }
        case 'string_set':
            if (!Array.isArray(value) || value.some((v) => typeof v !== 'string'))
                throw new ObservationValueError('string_set expected');
            return [...new Set(value)].sort();
    }
}
/**
 * The evidence hash drift compares (ADR-0089 §5): SHA-256 over canonical
 * `(path, element_key, value_type, value_json, status, complete)`.
 */
export function observationValueSha256(o) {
    return sha256Text(canonical([o.path, o.element_key ?? null, o.value_type, o.value_json, o.status, o.complete ? 1 : 0]));
}
/**
 * Normalize a collector's draft into an observation (minus id). A secret- or e-mail-shaped value,
 * error text or element key fails here, before any insert — there is no redaction class for secrets.
 */
export function normalizeObservation(d) {
    const path = safePath(d.path);
    const at = path ?? '(a path that itself looks like a secret)';
    let value;
    try {
        value = d.status === 'ok' ? normalizeValue(d.value, d.value_type) : null;
    }
    catch (err) {
        if (err instanceof ObservationValueError)
            throw new ObservationValueError(`value at ${at}: ${err.message}`, { path, field: 'value' });
        throw err;
    }
    const value_json = canonical(value);
    if (enc.encode(value_json).byteLength > OBSERVATION_VALUE_MAX_BYTES)
        throw new ObservationValueError(`value at ${at} is larger than ${OBSERVATION_VALUE_MAX_BYTES} bytes`, { path, field: 'value' });
    for (const [field, text] of [['value', value_json], ['element_key', d.element_key ?? ''], ['path', d.path], ['error_code', d.error_code ?? '']]) {
        if (scrubSecrets(text) !== text) {
            throw new ObservationValueError(`${field} at ${at} looks like a secret or an e-mail address; it is never stored`, { path, field, secretShaped: true });
        }
    }
    // Error text is scrubbed (never refused): provider words stay readable, secrets don't.
    const error_detail = d.error_detail ? scrubSecrets(d.error_detail).replace(/\s+/g, ' ').slice(0, 300) : null;
    const base = {
        resource_key: d.resource_key, resource_type: d.resource_type, path: d.path, element_key: d.element_key ?? null,
        value_json, value_type: d.value_type, source_api: d.source_api, collected_at: new Date(d.collected_at).toISOString(),
        status: d.status, complete: d.complete, error_code: d.error_code ?? null, error_detail, redaction_class: d.redaction_class,
    };
    return { ...base, value_sha256: observationValueSha256(base) };
}
/** content_sha256 of a rule version: every judged field in canonical JSON. */
export function ruleContentSha256(c) {
    return sha256Text(canonical(c));
}
export function fixturesSha256(fixtures) {
    return sha256Text(canonical(fixtures));
}
/** param_binding_sha256: the values the evaluation actually used (two programs with the same values share it). */
export function paramBindingSha256(used) {
    const clean = {};
    for (const k of Object.keys(used).sort())
        clean[k] = used[k] === undefined ? null : used[k];
    return sha256Text(canonical(clean));
}
/** A stable id for an observation from its content and position (fixtures and replays). */
export function observationId(o, seq) {
    return `obs_${sha256Text(canonical([o.resource_key, o.path, o.element_key, o.collected_at, o.value_sha256, seq])).slice(0, 24)}`;
}
//# sourceMappingURL=canonical.js.map